import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { ordersApi, type Order } from "@/lib/api";
import { formatDateTime } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import { RequestComments } from "../OrderSupportRequest/RequestComments";
import { supportRequestQueryKeys } from "../OrderSupportRequest/supportRequest";
import { CancellationStepper, type CancellationStep } from "./CancellationStepper";
import {
  formatStepDate,
  formatTicketStatus,
  isTicketResolved,
  parseCancellationReason,
  type CancellationReason,
} from "./cancellation";

/**
 * Solicitação feita nesta sessão. No chamado, motivo e autor vêm da API (`cancellationTicketCreatedBy`
 * no pedido, `reason` em `/cancellation-ticket`); isto é só a reserva enquanto ela não os devolve.
 */
export interface CancellationRequest extends CancellationReason {
  requestedBy?: string;
}

const STATUS_BADGES = {
  cancelled: {
    label: "Cancelado",
    className: "border-border bg-muted text-muted-foreground",
  },
  inReview: {
    label: "Em análise",
    className:
      "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200",
  },
} as const;

const StatusHeader = ({ status }: { status: keyof typeof STATUS_BADGES }) => (
  <div className="flex items-center justify-between gap-4">
    <h2 className="text-[19px] font-bold leading-tight">Status do cancelamento</h2>
    <Badge variant="outline" className={cn("shrink-0 whitespace-nowrap px-3 py-1.5", STATUS_BADGES[status].className)}>
      {STATUS_BADGES[status].label}
    </Badge>
  </div>
);

const DataField = ({ label, value }: { label: string; value?: ReactNode }) => {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex flex-col gap-1.5">
      <dt className="text-[13px] text-muted-foreground">{label}</dt>
      <dd className="break-words text-[15px]">{value}</dd>
    </div>
  );
};

const DetailsBlock = ({ details }: { details: string }) => (
  <div className="flex flex-col gap-1 rounded-[10px] border bg-muted/30 px-[18px] py-4">
    <span className="text-[13px] text-muted-foreground">Detalhes informados</span>
    <p className="whitespace-pre-line break-words text-[14.5px] leading-relaxed">{details}</p>
  </div>
);

const SuccessBanner = ({ onStartNewOrder }: { onStartNewOrder: () => void }) => (
  <div className="flex flex-col gap-4 border-b px-7 py-6 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
    <div className="flex items-center gap-3.5">
      <span aria-hidden="true" className="h-6 w-6 shrink-0 rounded-full bg-primary" />
      <div className="flex flex-col gap-1">
        <p className="text-[17px] font-semibold leading-snug">Solicitação concluída</p>
      </div>
    </div>
    <Button onClick={onStartNewOrder} className="shrink-0">
      Fazer novo pedido
    </Button>
  </div>
);

interface DirectResultProps {
  order: Order;
  onStartNewOrder: () => void;
}

const DirectResult = ({ order, onStartNewOrder }: DirectResultProps) => {
  const reason = parseCancellationReason(order.cancellationReason);
  const cancelledAt = formatStepDate(order.cancelledAt) ?? "—";
  const steps: CancellationStep[] = [
    { label: "Solicitado", caption: cancelledAt, state: "done" },
    { label: "Cancelado", caption: cancelledAt, state: "final" },
  ];

  return (
    <>
      <SuccessBanner onStartNewOrder={onStartNewOrder} />
      <div className="flex flex-col gap-6 p-7">
        <StatusHeader status="cancelled" />
        <CancellationStepper steps={steps} className="max-w-[520px]" />
        <Separator />
        <dl className="grid gap-5 sm:grid-cols-3">
          <DataField label="Cancelado em" value={order.cancelledAt && formatDateTime(new Date(order.cancelledAt))} />
          <DataField label="Cancelado por" value={order.cancelledBy} />
          <DataField label="Motivo" value={reason?.reason} />
        </dl>
        {reason?.details && <DetailsBlock details={reason.details} />}
      </div>
    </>
  );
};

interface TicketResultProps {
  orderId: string;
  order: Order;
  ticketId: number;
  lastRequest: CancellationRequest | null;
  onStartNewOrder: () => void;
}

/**
 * Chamado aberto no Zendesk. Sem link pra lá (seção 3.2 do contrato): o status ao vivo vem de
 * `GET /orders/{id}/cancellation-ticket`, que pode trazer `subject`/`status`/`reason` nulos se o Zendesk falhar.
 */
const TicketResult = ({ orderId, order, ticketId, lastRequest, onStartNewOrder }: TicketResultProps) => {
  const queryClient = useQueryClient();
  const { data: ticket, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["cancellationTicket", orderId],
    queryFn: () => ordersApi.getCancellationTicket(orderId),
    staleTime: 60_000,
  });

  // "Atualizar status" também traz as respostas novas do atendimento.
  const handleRefresh = () => {
    refetch();
    queryClient.invalidateQueries({ queryKey: supportRequestQueryKeys.comments(orderId, ticketId) });
  };

  const isResolved = !!order.cancelledAt || isTicketResolved(ticket?.status);
  const openedAt = order.cancellationTicketCreatedAt ?? ticket?.createdAt;
  // A API vale em qualquer carregamento/dispositivo; a sessão cobre o intervalo até ela responder.
  const reason = parseCancellationReason(ticket?.reason) ?? lastRequest ?? parseCancellationReason(order.cancellationReason);
  const requestedBy = order.cancellationTicketCreatedBy ?? lastRequest?.requestedBy;
  const ticketStatus = formatTicketStatus(ticket?.status);

  const steps: CancellationStep[] = [
    { label: "Solicitado", caption: formatStepDate(openedAt) ?? "—", state: "done" },
    {
      label: "Em Andamento",
      caption: isResolved ? "Concluído" : "",
      state: isResolved ? "done" : "current",
    },
    {
      label: "Solicitação concluída",
      caption: isResolved ? formatStepDate(order.cancelledAt) ?? "Concluído" : "Aguardando",
      state: isResolved ? "final" : "pending",
    },
  ];

  return (
    <>
      {isResolved ? (
        <SuccessBanner onStartNewOrder={onStartNewOrder} />
      ) : (
        <div className="flex flex-col gap-4 border-b bg-amber-50 px-7 py-6 dark:bg-amber-950/30 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <div className="flex items-center gap-3.5">
            <span aria-hidden="true" className="h-6 w-6 shrink-0 rounded-full border-[5px] border-amber-500 bg-background" />
            <div className="flex flex-col gap-1">
              <p className="text-[17px] font-semibold leading-snug">Cancelamento solicitado</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isFetching}
            className="shrink-0 hover:bg-muted hover:text-foreground"
          >
            <RefreshCw className={cn("mr-2 h-4 w-4", isFetching && "animate-spin")} aria-hidden="true" />
            Atualizar status
          </Button>
        </div>
      )}

      <div className="flex flex-col gap-6 p-7">
        <StatusHeader status={isResolved ? "cancelled" : "inReview"} />
        <CancellationStepper steps={steps} />
        <Separator />
        <dl className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <DataField label="Ticket" value={ticketStatus ? `#${ticketId} · ${ticketStatus}` : `#${ticketId}`} />
          <DataField label="Aberto em" value={openedAt && formatDateTime(new Date(openedAt))} />
          <DataField label="Solicitado por" value={requestedBy} />
          <DataField label="Motivo" value={reason?.reason} />
        </dl>
        {isLoading ? (
          <Skeleton className="h-10 w-2/3" />
        ) : (
          <dl>
            <DataField label="Assunto" value={ticket?.subject} />
          </dl>
        )}
        {reason?.details && <DetailsBlock details={reason.details} />}
        <RequestComments orderId={orderId} zendeskTicketId={ticketId} />
      </div>
    </>
  );
};

interface CancellationResultProps {
  orderId: string;
  order: Order;
  lastRequest: CancellationRequest | null;
  onStartNewOrder: () => void;
}

export const CancellationResult = ({ orderId, order, lastRequest, onStartNewOrder }: CancellationResultProps) =>
  order.cancellationTicketId ? (
    <TicketResult
      orderId={orderId}
      order={order}
      ticketId={order.cancellationTicketId}
      lastRequest={lastRequest}
      onStartNewOrder={onStartNewOrder}
    />
  ) : (
    <DirectResult order={order} onStartNewOrder={onStartNewOrder} />
  );
